import { streamFleetEvents, fleetStreamConnectTimeoutMs, fleetStreamIdleTimeoutMs } from "@/transport/fleet"
import { jitterDelay } from "@/utils/network-schedule"

export function fleetReconnectDelay(delay: number, random = Math.random) {
  return jitterDelay(delay, Math.min(1_000, delay), random)
}

/** Owns CP stream recovery independently of every machine's coding connection. */
export function createFleetEventStreamController(input: {
  visible: () => boolean
  onEvent: (type: string, properties: Record<string, unknown>, signal: AbortSignal) => void
  stream?: typeof streamFleetEvents
  delay?: (ms: number) => number
}) {
  let disposed = false
  let active: AbortController | undefined
  let timer: ReturnType<typeof setTimeout> | undefined
  let retryDelay = 1_000
  let restart = false
  let connected = false
  let startedAt = 0
  let lastActivity = 0

  const pause = () => {
    clearTimeout(timer)
    timer = undefined
  }
  const schedule = () => {
    if (disposed || !input.visible()) return
    const delay = retryDelay
    retryDelay = Math.min(retryDelay * 2, 30_000)
    timer = setTimeout(() => {
      timer = undefined
      void connect()
    }, (input.delay ?? fleetReconnectDelay)(delay))
  }
  const connect = async () => {
    if (disposed || active || !input.visible()) return
    pause()
    const controller = new AbortController()
    active = controller
    connected = false
    startedAt = lastActivity = Date.now()
    try {
      await (input.stream ?? streamFleetEvents)({
        signal: controller.signal,
        onActivity: () => { lastActivity = Date.now() },
        onEvent: (type, properties) => {
          if (controller.signal.aborted) return
          if (type === "server.connected") {
            connected = true
            retryDelay = 1_000
          }
          input.onEvent(type, properties, controller.signal)
        },
      })
    } catch {
      // Retry transport failures without discarding the authoritative fleet snapshot.
    } finally {
      controller.abort()
      active = undefined
    }
    if (disposed) return
    if (restart) {
      restart = false
      void connect()
    } else schedule()
  }
  const resume = () => {
    if (disposed || !input.visible()) return
    pause()
    retryDelay = 1_000
    if (active) {
      const age = Date.now() - (connected ? lastActivity : startedAt)
      if (age < (connected ? fleetStreamIdleTimeoutMs : fleetStreamConnectTimeoutMs)) return
      // Wait for the old reader to be cancelled before opening its replacement.
      restart = true
      active.abort()
    } else void connect()
  }
  return {
    resume,
    pause,
    dispose() {
      disposed = true
      pause()
      active?.abort()
    },
  }
}
